import type { CSSObject } from '@emotion/react'
import { useEffect, useRef } from 'react'
import { Portal } from 'react-portal'
import { HotKey } from '@opallabs/kuiper'
import { getTrackableComponent } from '@opallabs/quanta'
import {
  footerStyles,
  getBodyStyles,
  headerStyles,
  overlayStyles,
  OverlayStylesGeneratorArg,
  scrollWindowStyles,
} from './styles'

/**
 * @deprecated Migrate to using `Modal` in `@opallabs/lapidary/`.
 */

type Tracker = (arg: unknown) => void

export interface Props extends Partial<OverlayStylesGeneratorArg> {
  bodyStyleOverrides?: CSSObject
  footer?: React.ReactNode
  header?: React.ReactNode
  portalNode?: Element
  onClose: (e: React.SyntheticEvent, trackerFn?: Tracker) => void
  onEscapePress?: (e: React.SyntheticEvent, trackerFn?: Tracker) => void
  trackers?: { click?: Tracker; hotkey?: Tracker } | null
  visible: boolean
}

/**
 * @deprecated Migrate to the composable `@opallabs/lapidary` `Modal`.
 */
export const Modal: React.FC<React.PropsWithChildren<Props>> = ({
  backgroundColor = 'rgba(40, 50, 59, 0.95)',
  bodyStyleOverrides = {},
  children,
  footer = null,
  header = null,
  left = 0,
  onClose,
  onEscapePress,
  portalNode,
  right = 0,
  trackers = null,
  visible,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const scrollWindowRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bodyWrapperNode = document && document.querySelector('#body_wrapper')
    const baseWrapperNode = document && document.querySelector('#base_wrapper')

    if (visible) {
      if (
        bodyWrapperNode !== null &&
        typeof window.hideSelector !== 'undefined'
      ) {
        window.hideSelector('#body_wrapper')
      }
      if (
        baseWrapperNode !== null &&
        typeof window.hideSelector !== 'undefined'
      ) {
        window.hideSelector('#base_wrapper')
      }
    } else {
      if (
        bodyWrapperNode !== null &&
        typeof window.showSelector !== 'undefined'
      ) {
        window.showSelector('#body_wrapper')
      }
      if (
        baseWrapperNode !== null &&
        typeof window.showSelector !== 'undefined'
      ) {
        window.showSelector('#base_wrapper')
      }
    }

    return (): void => {
      if (
        bodyWrapperNode !== null &&
        typeof window.showSelector !== 'undefined'
      ) {
        window.showSelector('#body_wrapper')
      }
      if (
        baseWrapperNode !== null &&
        typeof window.showSelector !== 'undefined'
      ) {
        window.showSelector('#base_wrapper')
      }
    }
  }, [visible])

  const onOverlayClick: React.EventHandler<
    React.SyntheticEvent<HTMLDivElement>
  > = (e) => {
    const refs = [overlayRef, scrollWindowRef, bodyRef]
    const isOverlay = refs.find((ref) => ref.current === e.target)

    let trackerFn
    if (trackers !== null && trackers.click !== undefined) {
      trackerFn = trackers.click
    }

    if (isOverlay !== undefined) {
      onClose(e, trackerFn)
    }
  }

  const handleEscapePress: React.KeyboardEventHandler = (e) => {
    let trackerFn
    if (trackers !== null && trackers.hotkey !== undefined) {
      trackerFn = trackers.hotkey
    }

    if (onEscapePress !== undefined) {
      onEscapePress(e, trackerFn)
    } else {
      onClose(e, trackerFn)
    }
  }

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */
  return visible ? (
    <Portal node={portalNode}>
      <div
        css={overlayStyles({ backgroundColor, left, right })}
        onClick={onOverlayClick}
        ref={overlayRef}
        role="dialog"
      >
        <HotKey hotKey="esc" callback={handleEscapePress} />
        {header && <div css={headerStyles}>{header}</div>}
        <div
          css={scrollWindowStyles}
          onClick={onOverlayClick}
          ref={scrollWindowRef}
        >
          <div
            css={getBodyStyles(bodyStyleOverrides)}
            onClick={onOverlayClick}
            ref={bodyRef}
          >
            {children}
          </div>
        </div>
        {footer && <div css={footerStyles}>{footer}</div>}
      </div>
    </Portal>
  ) : null
}

export const TrackedModal = getTrackableComponent<Props>(Modal, 'Modal')
